// My Profile (page=profile): name, sign-in email, password.
// Subscription (page=subscription, alias page=account): plan, status, renewal,
// credits, and the Stripe customer portal (change plan, cancel, payment method,
// invoices). Reached from the left menu and from the header badges.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { authHeaders } from '../api';
import api, { type UsageSummary } from '../api';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.ocnexus.dev';

/** Customer-facing plan name. Internal plan keys never reach the UI. */
export function planDisplayName(plan: string | null | undefined): string {
  if (plan === 'forge') return 'OCNexus Starter';
  if (plan === 'factory') return 'OCNexus Pro';
  return 'No plan';
}

/** Short header label for the plan badge. */
export function planBadgeLabel(plan: string | null | undefined): string {
  if (plan === 'forge') return 'Starter';
  if (plan === 'factory') return 'Pro';
  return 'No plan';
}

export function statusDisplayName(status: string | null | undefined): string {
  switch (status) {
    case 'active': return 'Active';
    case 'trialing': return 'Trial';
    case 'past_due': return 'Payment failed';
    case 'cancelled': case 'canceled': return 'Cancelled';
    case 'incomplete': return 'Payment pending';
    default: return status ? status : 'Unknown';
  }
}

/** Same policy the API enforces on password reset: 12+ chars, upper, lower, digit, symbol. */
export function passwordProblem(pw: string): string | null {
  if (pw.length < 12) return 'Use at least 12 characters.';
  if (!/[a-z]/.test(pw)) return 'Add a lowercase letter.';
  if (!/[A-Z]/.test(pw)) return 'Add an uppercase letter.';
  if (!/[0-9]/.test(pw)) return 'Add a number.';
  const allowedSymbols = "!@#$%^&*()_+-=[]{};'\\:\"|<>?,./`~";
  if (!Array.from(pw).some((character: string) => allowedSymbols.includes(character))) return 'Add a symbol such as !, @, #, or $.';
  return null;
}

function passwordErrorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

function passwordErrorMessage(error: unknown): string {
  switch (passwordErrorCode(error)) {
    case 'weak_password': {
      const reasons = (error as { reasons?: unknown }).reasons;
      return Array.isArray(reasons) && reasons.includes('pwned')
        ? 'Choose a different password. This password is known to have been leaked.'
        : 'Choose a stronger password: at least 12 characters, uppercase, lowercase, a number, and a symbol such as ! or @.';
    }
    case 'same_password': return 'Choose a password different from your current one.';
    case 'reauthentication_needed': return 'Verify this password change using a verification code.';
    case 'reauthentication_not_valid': return 'The verification code is invalid or expired. Send a new code and try again.';
    case 'session_expired':
    case 'session_not_found':
    case 'bad_jwt':
    case 'refresh_token_not_found':
    case 'refresh_token_already_used': return 'Your session has expired. Sign out, sign back in, and try again.';
    case 'over_request_rate_limit': return 'Too many requests. Wait a moment before trying again.';
    default:
      if (error && typeof error === 'object' && (error as { name?: unknown }).name === 'AuthSessionMissingError') {
        return 'Your session has expired. Sign out, sign back in, and try again.';
      }
      return 'Could not update your password. Please try again.';
  }
}

const card: React.CSSProperties = {
  border: '1px solid var(--border)', borderRadius: 10, padding: 'var(--space-5)',
  background: 'var(--surface1, transparent)', display: 'flex', flexDirection: 'column', gap: 12,
};
const label: React.CSSProperties = { fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: 1 };
const value: React.CSSProperties = { fontSize: 15, color: 'var(--text)' };
const input: React.CSSProperties = {
  width: '100%', padding: '10px 12px', background: 'var(--surface2)', border: '1px solid var(--border)',
  borderRadius: 7, color: 'var(--text)', fontSize: 14, fontFamily: 'var(--font-mono)',
};
const primaryBtn: React.CSSProperties = {
  padding: '10px 16px', border: 'none', borderRadius: 8, background: 'var(--accent, #00E5FF)', color: '#0B0B1A',
  fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 13,
};

function PageHeader({ title, sub }: { title: string; sub: string }) {
  return (
    <div>
      <h1 style={{ margin: 0, fontSize: 26 }}>{title}</h1>
      <p style={{ margin: '6px 0 0', color: 'var(--text-muted)' }}>{sub}</p>
    </div>
  );
}

function Message({ msg, feedbackRef }: { msg: { ok: boolean; text: string } | null; feedbackRef?: React.Ref<HTMLDivElement> }) {
  if (!msg) return null;
  return (
    <div ref={feedbackRef} tabIndex={feedbackRef ? -1 : undefined} role={msg.ok ? 'status' : 'alert'} style={{ color: msg.ok ? 'var(--green, #3ddc97)' : 'var(--red, #ff6b6b)', fontSize: 13 }}>
      {msg.text}
    </div>
  );
}

/* ─── My Profile ─────────────────────────────────────────────── */

export function ProfilePage({ go }: { go: (page: string) => void }) {
  const { user, subscriber, refreshSubscriber } = useAuth();
  const sub = subscriber as unknown as Record<string, unknown> | null;
  const currentName = typeof sub?.full_name === 'string' ? sub.full_name : '';

  const [name, setName] = useState(currentName);
  const [nameBusy, setNameBusy] = useState(false);
  const [nameMsg, setNameMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const saveName = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setNameMsg(null);
    const trimmed = name.trim().slice(0, 200);
    if (!trimmed) { setNameMsg({ ok: false, text: 'Enter your name.' }); return; }
    if (!user?.id) return;
    setNameBusy(true);
    try {
      // Clients may only update profile columns on their own row (DB-enforced).
      const { error } = await supabase.from('subscribers').update({ full_name: trimmed }).eq('id', user.id);
      if (error) { setNameMsg({ ok: false, text: 'Could not save your name. Please try again.' }); return; }
      await refreshSubscriber?.();
      setNameMsg({ ok: true, text: 'Name saved.' });
    } finally {
      setNameBusy(false);
    }
  }, [name, user?.id, refreshSubscriber]);

  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const pwFeedbackRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // A disabled submit button loses focus, and a result below it may be
    // outside the shell's scroll viewport. Focus the rendered result so the
    // browser brings both errors and acknowledgements into view.
    if (pwMsg) pwFeedbackRef.current?.focus();
  }, [pwMsg]);
  const [pwVerificationRequired, setPwVerificationRequired] = useState(false);
  const [pwNonce, setPwNonce] = useState('');
  const [pwCodeBusy, setPwCodeBusy] = useState(false);

  const sendPasswordCode = useCallback(async () => {
    setPwMsg(null);
    setPwCodeBusy(true);
    setPwNonce('');
    try {
      // Sending a code is an explicit user action, never an automatic retry.
      const { error } = await supabase.auth.reauthenticate();
      if (error) {
        const message = passwordErrorMessage(error);
        setPwMsg({ ok: false, text: message.startsWith('Your session') ? message : 'Could not send a verification code. Please try again.' });
        return;
      }
      setPwMsg({ ok: true, text: 'Verification code sent to your sign-in email or phone. Enter it below, then update your password.' });
    } catch {
      setPwMsg({ ok: false, text: 'Could not send a verification code. Check your connection and try again.' });
    } finally {
      setPwCodeBusy(false);
    }
  }, []);

  const changePassword = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg(null);
    const problem = passwordProblem(pw);
    if (problem) { setPwMsg({ ok: false, text: problem }); return; }
    if (pw !== pw2) { setPwMsg({ ok: false, text: 'The two passwords do not match.' }); return; }
    setPwBusy(true);
    try {
      const { data, error } = await supabase.auth.updateUser({ password: pw, ...(pwNonce.trim() ? { nonce: pwNonce.trim() } : {}) });
      if (error) {
        const code = passwordErrorCode(error);
        if (code === 'reauthentication_needed' || code === 'reauthentication_not_valid') setPwVerificationRequired(true);
        setPwMsg({ ok: false, text: passwordErrorMessage(error) });
        return;
      }
      if (!data.user) {
        setPwMsg({ ok: false, text: 'Could not update your password. Please try again.' });
        return;
      }
      setPw(''); setPw2('');
      setPwNonce('');
      setPwVerificationRequired(false);
      setPwMsg({ ok: true, text: 'Password updated. Use it next time you sign in with email and password.' });
    } catch {
      setPwMsg({ ok: false, text: 'Could not update your password. Check your connection and try again.' });
    } finally {
      setPwBusy(false);
    }
  }, [pw, pw2, pwNonce]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)', maxWidth: 720 }}>
      <PageHeader title="My profile" sub="Your name, sign-in email, and password." />

      <section style={card} aria-labelledby="profile-name">
        <div id="profile-name" style={label}>Name</div>
        <form onSubmit={saveName} style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <input type="text" autoComplete="name" value={name} placeholder="Your name"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            style={{ ...input, flex: '1 1 260px', width: 'auto' }} aria-label="Name" />
          <button type="submit" style={primaryBtn} disabled={nameBusy}>{nameBusy ? 'Saving…' : 'Save'}</button>
        </form>
        <Message msg={nameMsg} />
      </section>

      <section style={card} aria-labelledby="profile-email">
        <div id="profile-email" style={label}>Sign-in email</div>
        <div style={value}>{user?.email ?? 'Signed in'}</div>
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>To change your sign-in email, contact support.</div>
      </section>

      <section style={card} aria-labelledby="profile-password">
        <div id="profile-password" style={label}>Password</div>
        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 13 }}>
          Set or change the password you use to sign in with email. At least 12 characters, with upper and lower case, a number, and a symbol.
        </p>
        <form onSubmit={changePassword} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input type="password" autoComplete="new-password" placeholder="New password" value={pw}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPw(e.target.value)} style={input} aria-label="New password" />
          <input type="password" autoComplete="new-password" placeholder="Confirm new password" value={pw2}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPw2(e.target.value)} style={input} aria-label="Confirm new password" />
          {pwVerificationRequired && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button type="button" onClick={() => { void sendPasswordCode(); }} style={primaryBtn} disabled={pwBusy || pwCodeBusy}>
                {pwCodeBusy ? 'Sending…' : 'Send verification code'}
              </button>
              <input type="text" autoComplete="one-time-code" placeholder="Verification code" value={pwNonce}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPwNonce(e.target.value)} style={input} aria-label="Verification code" />
            </div>
          )}
          <div>
            <button type="submit" style={primaryBtn} disabled={pwBusy || pwCodeBusy}>{pwBusy ? 'Saving…' : 'Update password'}</button>
          </div>
          <Message msg={pwMsg} feedbackRef={pwFeedbackRef} />
        </form>
      </section>

      <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
        Plan, credits, payment method and invoices are on{' '}
        <button type="button" onClick={() => go('subscription')}
          style={{ background: 'none', border: 'none', padding: 0, color: 'var(--accent, #00E5FF)', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}>
          Subscription
        </button>.
      </div>
    </div>
  );
}

/* ─── Subscription ───────────────────────────────────────────── */

export function SubscriptionPage({ go }: { go: (page: string) => void }) {
  const { subscriber } = useAuth();
  const sub = subscriber as unknown as Record<string, unknown> | null;
  const plan = typeof sub?.plan === 'string' ? sub.plan : null;
  const status = typeof sub?.status === 'string' ? sub.status : null;
  const hasBillingAccount = typeof sub?.stripe_customer_id === 'string' && (plan === 'forge' || plan === 'factory');
  const periodEnd = typeof sub?.current_period_end === 'string' ? new Date(sub.current_period_end) : null;
  const cancelAtPeriodEnd = sub?.cancel_at_period_end === true;
  const justPaid = useMemo(() => new URLSearchParams(window.location.search).get('checkout') === 'success', []);

  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [usage, setUsage] = useState<UsageSummary | null>(null);

  React.useEffect(() => {
    let active = true;
    void api.getUsage()
      .then((summary: UsageSummary) => { if (active) setUsage(summary); })
      .catch(() => { if (active) setUsage(null); });
    return () => { active = false; };
  }, []);

  const openPortal = useCallback(async () => {
    setPortalError(null);
    setPortalBusy(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API_BASE}/api/billing/portal-session`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: '{}',
      });
      const body = await res.json().catch(() => ({})) as { url?: string; error?: string };
      if (res.ok && body.url) { window.location.assign(body.url); return; }
      if (body.error === 'no_subscription') setPortalError("You don't have a paid subscription yet. Choose a plan first.");
      else if (body.error === 'billing_portal_not_configured') setPortalError('Subscription management is temporarily unavailable. Contact support and we will make the change for you.');
      else setPortalError('Could not open subscription management. Please try again.');
    } catch {
      setPortalError('Could not open subscription management. Please try again.');
    } finally {
      setPortalBusy(false);
    }
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)', maxWidth: 720 }}>
      <PageHeader title="Subscription" sub="Your plan, credits, payment method, and invoices." />

      {justPaid && (
        <div role="status" style={{ ...card, borderColor: 'var(--green, #3ddc97)' }}>
          Payment received. Your plan activates as soon as Stripe confirms it, usually within a minute. Refresh if it still shows pending.
        </div>
      )}

      <section style={card} aria-labelledby="sub-plan">
        <div id="sub-plan" style={label}>Plan</div>
        <div style={value}>
          {planDisplayName(plan)}
          {hasBillingAccount && <span style={{ color: 'var(--text-muted)' }}> · {statusDisplayName(status)}</span>}
        </div>
        {hasBillingAccount && periodEnd && !Number.isNaN(periodEnd.getTime()) && (
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            {cancelAtPeriodEnd ? 'Ends on ' : 'Renews on '}{periodEnd.toLocaleDateString()}
          </div>
        )}
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          Your plan includes credits every month (Starter 5,000 · Pro 10,000). Included credits reset monthly and do not roll over; top-up credits never expire.
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {hasBillingAccount ? (
            <button type="button" style={primaryBtn} onClick={() => void openPortal()} disabled={portalBusy}>
              {portalBusy ? 'Opening…' : 'Change plan, cancel, payment method & invoices'}
            </button>
          ) : (
            <button type="button" style={primaryBtn} onClick={() => go('plan')}>
              Choose a plan
            </button>
          )}
        </div>
        {portalError && <div role="alert" style={{ color: 'var(--red, #ff6b6b)', fontSize: 13 }}>{portalError}</div>}
      </section>

      <section style={card} aria-labelledby="sub-credits">
        <div id="sub-credits" style={label}>Forge credits</div>
        <div style={value}>{usage ? `${usage.creditsRemaining.toLocaleString()} credits` : 'Loading credits…'}</div>
        {usage && <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          Spendable MCP wallet balance
        </div>}
      </section>

      <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
        Name and password are on{' '}
        <button type="button" onClick={() => go('profile')}
          style={{ background: 'none', border: 'none', padding: 0, color: 'var(--accent, #00E5FF)', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}>
          My profile
        </button>.
      </div>
    </div>
  );
}

export default SubscriptionPage;
